import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    grecaptcha?: {
      render: (
        container: HTMLElement,
        params: { sitekey: string; size: 'invisible'; callback: (token: string) => void }
      ) => number;
      execute: (widgetId?: number) => void;
      reset: (widgetId?: number) => void;
    };
    onRecaptchaApiLoad?: () => void;
  }
}

const SCRIPT_ID = 'recaptcha-api-script';

function loadScriptOnce(): Promise<void> {
  if (window.grecaptcha) return Promise.resolve();
  return new Promise((resolve) => {
    if (document.getElementById(SCRIPT_ID)) {
      window.onRecaptchaApiLoad = resolve;
      return;
    }
    window.onRecaptchaApiLoad = resolve;
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaApiLoad&render=explicit';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  });
}

/**
 * Invisible reCAPTCHA v2, explicitly rendered/executed — same site key + UX as the legacy
 * Twig `g-recaptcha` button, just wired programmatically so React Hook Form's own
 * `handleSubmit` (client validation) can run first, and the token is only fetched once the
 * form is actually valid.
 */
export function useRecaptcha(siteKey: string) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);
  const resolverRef = useRef<((token: string) => void) | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadScriptOnce().then(() => {
      if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.grecaptcha!.render(containerRef.current, {
        sitekey: siteKey,
        size: 'invisible',
        callback: (token: string) => resolverRef.current?.(token),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  const execute = (): Promise<string> =>
    new Promise((resolve) => {
      resolverRef.current = resolve;
      if (widgetIdRef.current !== null) {
        window.grecaptcha?.execute(widgetIdRef.current);
      }
    });

  return { containerRef, execute };
}
